<template>
  <div>
    <seats-modal-shell
      :visible="visible && step === 'select'"
      title="Comprar licenças"
      wide
      @close="$emit('close')">
      <div>
        <p class="seats-modal-text">
          As licenças novas entram na assinatura livres, para você ocupar com quem quiser. Agora é cobrado o valor proporcional até o fim do ciclo.
        </p>

        <span class="seats-modal-label">Planos</span>
        <div class="purchase-plan-list">
          <div
            v-for="plan in purchasablePlans"
            :key="plan.code"
            class="purchase-plan-row"
            :style="{ borderLeftColor: planStyle(plan.code).borderColor }">
            <div class="purchase-plan-info">
              <div class="purchase-plan-heading">
                <plan-badge :plan="plan.name" :code="plan.code" />
                <span class="purchase-plan-price">{{ formatCurrency(Number(plan.monthlyFee)) }}/mês cada</span>
                <campaign-offer-note
                  v-if="campaignDiscountOf(plan)"
                  :plan-name="plan.name"
                  :discount="campaignDiscountOf(plan)" />
              </div>
              <span v-if="benefitsOf(plan).length > 0" class="purchase-plan-benefits">{{ benefitsOf(plan).join(' · ') }}</span>
            </div>
            <div class="purchase-stepper">
              <button
                type="button"
                aria-label="remover licença"
                :disabled="isSubmitting || quantityOf(plan.code) === 0"
                @click="bumpQuantity(plan.code, -1)">−</button>
              <span class="purchase-stepper-qty">{{ quantityOf(plan.code) }}</span>
              <button
                type="button"
                aria-label="adicionar licença"
                :disabled="isSubmitting || totalQuantity >= maxQuantity"
                @click="bumpQuantity(plan.code, 1)">+</button>
            </div>
          </div>
        </div>
        <p v-if="totalQuantity >= maxQuantity" class="seats-modal-warning">Até {{ maxQuantity }} licenças por compra.</p>

        <p v-if="previewLoading && !preview" class="seats-modal-text" style="margin-top: 1.25rem;">Calculando o valor proporcional...</p>
        <p v-else-if="previewError" class="seats-modal-error">{{ previewError }}</p>
        <div
          v-else-if="preview"
          class="seats-modal-summary purchase-summary"
          :class="{ 'purchase-summary--updating': previewIsStale }">
          <span class="purchase-summary-title">Mensalidade a mais a partir do próximo ciclo</span>
          <div v-for="line in previewLines" :key="line.planName" class="seats-modal-summary-row">
            <span>{{ line.quantity }} × {{ line.name }}</span>
            <span>{{ formatCurrency(line.monthlyFee) }}</span>
          </div>
          <div class="seats-modal-summary-row purchase-summary-divider">
            <span>Proporcional até {{ formatSeatDate(preview.cycleEnd) }}</span>
            <span>{{ formatCurrency(preview.proratedFee) }}</span>
          </div>
          <!-- A mensalidade do próximo ciclo já foi emitida e paga sem estas licenças:
               ela vem adiantada aqui, senão as licenças usariam aquele ciclo de graça. -->
          <div v-if="preview.nextCycleAlreadyPaid" class="seats-modal-summary-row" style="margin-top: 0.25rem;">
            <span>Mensalidade do próximo ciclo, já faturado</span>
            <span>{{ formatCurrency(preview.nextCycleFee) }}</span>
          </div>
          <div class="seats-modal-summary-row seats-modal-summary-row--total" style="margin-top: 0.625rem;">
            <span>Total a cobrar agora</span>
            <span style="white-space: nowrap;">{{ formatCurrency(preview.totalFee) }}</span>
          </div>
        </div>

        <div class="seats-modal-footer">
          <button type="button" class="seats-modal-button seats-modal-button--secondary" @click="$emit('close')">Cancelar</button>
          <button type="button" class="seats-modal-button seats-modal-button--primary" :disabled="continueBlocked" @click="step = 'payment'">
            Continuar para pagamento
          </button>
        </div>
      </div>
    </seats-modal-shell>

    <payment-method-modal
      :visible="visible && step === 'payment'"
      :loading="isSubmitting"
      :subscription-error="error"
      :show-subscription-payment-note="false"
      @close="$emit('close')"
      @confirm="submit" />

    <charge-generated-modal
      :visible="visible && step === 'done'"
      :total-formatted="purchaseTotalFormatted"
      :user-count="purchaseQuantity"
      count-label="Licenças"
      :due-date-formatted="purchaseDueDateFormatted"
      :payment-link="purchasePaymentLink"
      :subtitle="purchaseSubtitle"
      navigate-label="Voltar para as licenças"
      :show-plan-model-disclaimer="false"
      @close="$emit('close')"
      @navigate="$emit('close')" />
  </div>
</template>

<script>
import { previewSeatPurchase, purchaseSeats } from '@/services/subscription'
import { formatCurrency } from '@/utils/currency'
import { friendlyPlainText } from '@/utils/error-message'
import { formatSeatDate } from '@/utils/seats'
import { getPlanBadgeStyle, getPlanDisplayName } from '@/utils/plan-badge'
import { billingTypeForPaymentMethod } from '@/utils/billing-type'
import { planBenefitLabels } from '../../Subscription/pricing-comparison'
import { campaignDiscount } from '../../Subscription/plan-showcase'
import CampaignOfferNote from '../../Subscription/CampaignOfferNote.vue'
import PlanBadge from '../PlanBadge.vue'
import PaymentMethodModal from '../../Subscription/PaymentMethodModal.vue'
import ChargeGeneratedModal from '../../Subscription/ChargeGeneratedModal.vue'
import SeatsModalShell from './SeatsModalShell.vue'

const MAX_SEATS_PER_PURCHASE = 500
const PREVIEW_DEBOUNCE_MS = 400
const PAYMENT_SUBTITLE = 'Realize o pagamento para liberar as licenças. Elas aparecem livres assim que o pagamento confirmar.'

function seatCountLabel (count) {
  return count === 1 ? '1 licença' : `${count} licenças`
}

export default {
  name: 'PurchaseSeatsModal',
  components: {
    CampaignOfferNote,
    PlanBadge,
    PaymentMethodModal,
    ChargeGeneratedModal,
    SeatsModalShell
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    pricingList: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      quantities: {},
      preview: null,
      previewLoading: false,
      previewError: '',
      previewTimer: null,
      previewRequestId: 0,
      isSubmitting: false,
      error: '',
      purchase: null,
      step: 'select',
      maxQuantity: MAX_SEATS_PER_PURCHASE
    }
  },
  computed: {
    // Primeiro os planos com IA4COM, depois os sem; cada grupo do mais caro ao mais barato.
    purchasablePlans () {
      return this.pricingList
        .filter(plan => Number(plan.monthlyFee) > 0)
        .sort((first, second) => {
          const firstHasIa4com = first.hasIa4com === true
          if (firstHasIa4com !== (second.hasIa4com === true)) return firstHasIa4com ? -1 : 1
          return Number(second.monthlyFee) - Number(first.monthlyFee)
        })
    },
    // Só vai para o backend o plano com quantidade: zero não é item da compra.
    selectedItems () {
      return this.purchasablePlans
        .filter(plan => this.quantityOf(plan.code) > 0)
        .map(plan => ({ planName: plan.code, quantity: this.quantityOf(plan.code) }))
    },
    totalQuantity () {
      return this.selectedItems.reduce((total, item) => total + item.quantity, 0)
    },
    previewLines () {
      if (!this.preview || !Array.isArray(this.preview.items)) return []
      return this.preview.items.map(item => ({
        planName: item.planName,
        name: this.planNameOf(item.planName),
        quantity: item.quantity,
        monthlyFee: item.baseMonthlyFee * item.quantity
      }))
    },
    previewIsStale () {
      return this.previewLoading || this.previewTimer !== null
    },
    continueBlocked () {
      return this.selectedItems.length === 0 || this.previewIsStale || this.preview === null || !!this.previewError
    },
    purchaseTotalFormatted () {
      return this.purchase && this.purchase.value != null ? formatCurrency(this.purchase.value) : ''
    },
    purchaseDueDateFormatted () {
      return this.purchase && this.purchase.dueDate ? formatSeatDate(this.purchase.dueDate) : ''
    },
    purchasePaymentLink () {
      return this.purchase && this.purchase.paymentUrl ? this.purchase.paymentUrl : ''
    },
    purchaseQuantity () {
      return this.purchase && typeof this.purchase.quantity === 'number' ? this.purchase.quantity : 0
    },
    // "3 licenças: 2 × Negociação, 1 × Ilimitado."
    purchaseSubtitle () {
      if (!this.purchase || !Array.isArray(this.purchase.items) || this.purchase.items.length === 0) return PAYMENT_SUBTITLE
      const lines = this.purchase.items.map(item => `${item.quantity} × ${this.planNameOf(item.planName)}`)
      return `${seatCountLabel(this.purchaseQuantity)}: ${lines.join(', ')}. ${PAYMENT_SUBTITLE}`
    }
  },
  watch: {
    visible (isVisible) {
      if (isVisible) this.reset()
    }
  },
  beforeDestroy () {
    this.cancelScheduledPreview()
  },
  methods: {
    formatCurrency,
    formatSeatDate,
    quantityOf (planCode) {
      return this.quantities[planCode] || 0
    },
    planNameOf (planCode) {
      const plan = this.pricingList.find(candidate => candidate.code === planCode)
      return plan ? plan.name : getPlanDisplayName(planCode)
    },
    planStyle (planCode) {
      return getPlanBadgeStyle(planCode)
    },
    benefitsOf (plan) {
      return planBenefitLabels(plan)
    },
    campaignDiscountOf (plan) {
      return campaignDiscount(plan, Date.now())
    },
    bumpQuantity (planCode, delta) {
      const next = Math.max(0, this.quantityOf(planCode) + delta)
      if (delta > 0 && this.totalQuantity >= MAX_SEATS_PER_PURCHASE) return
      this.$set(this.quantities, planCode, next)
      this.schedulePreview()
    },
    reset () {
      this.quantities = {}
      this.cancelScheduledPreview()
      this.previewRequestId++
      this.preview = null
      this.previewLoading = false
      this.previewError = ''
      this.isSubmitting = false
      this.error = ''
      this.purchase = null
      this.step = 'select'
    },
    cancelScheduledPreview () {
      if (this.previewTimer === null) return
      clearTimeout(this.previewTimer)
      this.previewTimer = null
    },
    // Cliques seguidos no +/−: espera parar e mantém o valor anterior na tela
    // enquanto recalcula, em vez de sumir com o card a cada clique.
    schedulePreview () {
      this.cancelScheduledPreview()
      if (this.selectedItems.length === 0) {
        this.previewRequestId++
        this.preview = null
        this.previewLoading = false
        this.previewError = ''
        return
      }
      this.previewTimer = setTimeout(() => {
        this.previewTimer = null
        this.fetchPreview()
      }, PREVIEW_DEBOUNCE_MS)
    },
    async fetchPreview () {
      this.previewError = ''
      this.error = ''
      if (this.selectedItems.length === 0) {
        this.preview = null
        return
      }
      this.previewRequestId++
      const requestId = this.previewRequestId
      this.previewLoading = true
      try {
        const preview = await previewSeatPurchase(this.selectedItems)
        if (requestId === this.previewRequestId) this.preview = preview
      } catch (err) {
        if (requestId === this.previewRequestId) {
          this.preview = null
          this.previewError = friendlyPlainText(err, 'Não foi possível calcular o valor proporcional. Tente novamente.')
        }
      } finally {
        if (requestId === this.previewRequestId) this.previewLoading = false
      }
    },
    async submit ({ selectedPaymentMethod }) {
      if (this.isSubmitting) return
      const paymentWindow = window.open('', '_blank')
      let paymentWindowUsed = false
      this.isSubmitting = true
      this.error = ''
      try {
        const billingType = billingTypeForPaymentMethod(selectedPaymentMethod)
        const purchase = await purchaseSeats(this.selectedItems, billingType)
        this.purchase = purchase
        this.step = 'done'
        if (purchase.paymentUrl && paymentWindow) {
          paymentWindow.opener = null
          paymentWindow.location.href = purchase.paymentUrl
          paymentWindowUsed = true
        }
      } catch (err) {
        this.error = friendlyPlainText(err, 'Não foi possível gerar a cobrança das licenças. Tente novamente.')
      } finally {
        this.isSubmitting = false
        if (paymentWindow && !paymentWindowUsed) paymentWindow.close()
      }
    }
  }
}
</script>

<style scoped>
.purchase-summary {
  transition: opacity 0.15s ease;
}
.purchase-summary--updating {
  opacity: 0.55;
}
.purchase-summary-title {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
}
.purchase-summary-divider {
  border-top: 1px solid #e5e7eb;
  padding-top: 0.625rem;
  margin-top: 0.625rem !important;
}
.purchase-plan-list {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}
.purchase-plan-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-left: 3px solid transparent;
  border-bottom: 1px solid #f3f4f6;
}
.purchase-plan-row:last-child {
  border-bottom: none;
}
.purchase-plan-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}
.purchase-plan-heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.purchase-plan-price {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}
.purchase-plan-benefits {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.35;
}
.purchase-stepper {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  background-color: #ffffff;
  overflow: hidden;
}
.purchase-stepper button {
  width: 2rem;
  height: 2rem;
  border: none;
  background-color: #ffffff;
  color: #374151;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
}
.purchase-stepper button:hover:not(:disabled) {
  background-color: #f3f4f6;
}
.purchase-stepper button:disabled {
  color: #d1d5db;
  cursor: not-allowed;
}
.purchase-stepper-qty {
  min-width: 2.25rem;
  text-align: center;
  font-weight: 700;
  font-size: 0.9375rem;
}
</style>



// WEBPACK FOOTER //
// src/components/Users/seats/PurchaseSeatsModal.vue